// src/encounter/services/use-note-card-reads.ts
//
// The extra reads the A&P editor makes while `phrasePieces` is on, for chart links and their value
// cards. No new endpoint:
//   · LABS (`labs.set:<id>`, `labs.latest:<LOINC>`): the chart Labs tab's
// own two reads — the lab desk list (`POST /api/Patients/GetPatientLabDetails`) and the lab
// requests' Reports in one read (`GET /api/Patients/{id}/LabRequests/Reports?labRequestId=…`) — through
// the same query options and cache keys (`patients/services/use-patient-lab-results.ts`).
//   · VITALS HISTORY: the patient-wide `GET /api/Patients/GetVitalsList` the appointment Vitals tab
//     draws its sparklines from (`usePatientVitalsList`), for a vital card's trend and reading count.
//
// OFF BY DEFAULT. The A&P note editor passes a patient id only while `phrasePieces` is on, so with the
// switch off nothing is requested and nothing changes.
//
// `refetch` is the FRESH read a piece's Refresh needs (R17): the lab list again, then the requests'
// Reports again, and the vitals history again, for this patient — never served from cache.
import { useQueryClient } from '@tanstack/react-query';
import {
  isResultsNotDeployed,
  labRequestReportsQuery,
  patientLabRequestsQuery,
  toLabRequestReportsEntries,
  useLabRequestReports,
  usePatientLabRequests,
} from '@/patients/services/use-patient-lab-results';
import { usePatientVitalsList } from '@/scheduling/services/use-patient-vitals-list';
import type { PatientVital } from '@/types/vitals-api';
import { buildNoteLabReads, labRequestIdsOf, type NoteLabReads } from '../lib/to-chart-labs';
import { toChartRead, type ChartRead, type ChartReads } from '../lib/to-chart-snapshot';

/** Settle a promise into the query-like shape `buildNoteLabReads` reads. */
async function settled<T>(p: Promise<T>): Promise<{ data?: T; isError?: boolean; error?: unknown }> {
  try {
    return { data: await p };
  } catch (error) {
    return { isError: true, error };
  }
}

export interface NoteCardReads {
  labs: ChartRead<NoteLabReads> | undefined;
  vitalsHistory: ChartRead<PatientVital[]> | undefined;
  refetchLabs: () => Promise<ChartRead<NoteLabReads>>;
  refetchVitalsHistory: () => Promise<ChartRead<PatientVital[]>>;
}

/** `patientId` undefined (the switch off) → every query is disabled and nothing is read. */
export function useNoteCardReads(patientId: string | undefined): NoteCardReads {
  const queryClient = useQueryClient();
  const list = usePatientLabRequests(patientId);
  const reports = useLabRequestReports(patientId, labRequestIdsOf(list.data));
  const vitals = usePatientVitalsList({ patientId, enabled: !!patientId });

  const refetchLabs = async (): Promise<ChartRead<NoteLabReads>> => {
    if (!patientId) return { status: 'loading' };
    const fresh = await settled(queryClient.fetchQuery({ ...patientLabRequestsQuery(patientId), staleTime: 0 }));
    const ids = labRequestIdsOf(fresh.data);
    const freshReports = toLabRequestReportsEntries(
      ids,
      await settled(queryClient.fetchQuery({ ...labRequestReportsQuery(patientId, ids), staleTime: 0 })),
    );
    return buildNoteLabReads(fresh, freshReports, isResultsNotDeployed);
  };

  const refetchVitalsHistory = async (): Promise<ChartRead<PatientVital[]>> => {
    if (!patientId) return { status: 'loading' };
    return historyRead(toChartRead(await vitals.refetch()));
  };

  return {
    labs: patientId ? buildNoteLabReads(list, reports, isResultsNotDeployed) : undefined,
    vitalsHistory: patientId ? historyRead(toChartRead(vitals)) : undefined,
    refetchLabs,
    refetchVitalsHistory,
  };
}

/** The GetVitalsList envelope → its rows. A body that is not a list is a failed read, never "none". */
function historyRead(read: ChartRead<{ vitalHistoryRequest: PatientVital[] | null }>): ChartRead<PatientVital[]> {
  if (read.status !== 'ok') return { status: read.status };
  const rows = read.data?.vitalHistoryRequest;
  return Array.isArray(rows) ? { status: 'ok', data: rows } : { status: 'error' };
}

/**
 * The page's reads with the card reads added: lab links resolve from `labs`, vital lines carry their
 * card fields from `vitalsHistory`, and Refresh re-reads the page's reads AND these, all fresh, for
 * the same patient.
 */
export function withCardReads(reads: ChartReads, card: NoteCardReads): ChartReads {
  return {
    ...reads,
    labs: card.labs,
    vitalsHistory: card.vitalsHistory,
    refetch: async () => {
      const [base, labs, vitalsHistory] = await Promise.all([
        reads.refetch ? reads.refetch() : Promise.resolve(reads),
        card.refetchLabs(),
        card.refetchVitalsHistory(),
      ]);
      return { ...base, labs, vitalsHistory };
    },
  };
}
